import { APPLICANT_STATUS_LABEL } from "@/features/recruitment/applicant-vocabulary";
import type { ApplicantStatus } from "@/features/recruitment/schemas/applicant.schema";

/**
 * One vocabulary for the six applicant statuses: which of them are a funnel,
 * what each is called, and what colour each is allowed to be.
 *
 * The badge on a board card, the bars in the funnel report and the column
 * headers all read from here, so a status cannot be `success` green in one
 * place and `warning` amber in another. Reviews solved the same problem the
 * same way (reviews.constants.ts, PARTICIPANT_STATUS_TONE).
 *
 * Labels are NOT redefined here. `applicant-vocabulary.ts` already owns the
 * six words the board says, so this file re-exports them rather than opening a
 * second place for "On hold" to become "Hold".
 */

export { APPLICANT_STATUS_LABEL };

/// Visual tone, the same six-value vocabulary the reviews pills use. Colour is
/// never the only signal: every consumer pairs a tone with its label.
export type RecruitmentTone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";

/**
 * The four statuses that are actually a funnel, in order.
 *
 * A candidate moves open -> replied -> shortlisted -> accepted. Nothing else
 * is forward motion, which is why the list is four long and not six.
 */
export const PROGRESSION_STATUSES = [
  "open",
  "replied",
  "shortlisted",
  "accepted",
] as const satisfies readonly ApplicantStatus[];

export type ProgressionStatus = (typeof PROGRESSION_STATUSES)[number];

/**
 * The two statuses that leave the funnel.
 *
 * `hold` is parked (the candidate is real, the decision is not made) and
 * `rejected` is terminal. They are grouped because they behave the same way
 * in every chart: they are counted, and they are counted OUTSIDE the ramp.
 */
export const PARKED_STATUSES = ["hold", "rejected"] as const satisfies readonly ApplicantStatus[];

export type ParkedStatus = (typeof PARKED_STATUSES)[number];

/**
 * Tone per status.
 *
 * `hold` is `warning` rather than `neutral` on purpose: a parked candidate is
 * somebody waiting on a decision this team owes them, and a grey pill reads as
 * "nothing to do here". `open` is the quiet one instead, because a board is
 * mostly `open` and six loud columns tell a reader nothing.
 */
export const APPLICANT_STATUS_TONE: Record<ApplicantStatus, RecruitmentTone> = {
  open: "neutral",
  replied: "info",
  shortlisted: "brand",
  hold: "warning",
  accepted: "success",
  rejected: "danger",
};

/**
 * The shore's funnel report renames exactly one column: the pipeline's `open`
 * is the report's `applied`, because a recruiter reading a funnel says "how
 * many applied", not "how many are open" (shore funnel.types.ts documents the
 * rename). Every other count key is the status word itself.
 */
export const FUNNEL_COLUMN_BY_STATUS = {
  open: "applied",
  replied: "replied",
  shortlisted: "shortlisted",
  hold: "hold",
  accepted: "accepted",
  rejected: "rejected",
} as const satisfies Record<ApplicantStatus, string>;

export type FunnelStatusKey = (typeof FUNNEL_COLUMN_BY_STATUS)[ApplicantStatus];

/// The report's own word for the renamed column, so a bar is not labelled
/// "Open" under a heading that says "Applied".
export const FUNNEL_STAGE_LABEL: Record<FunnelStatusKey, string> = {
  applied: "Applied",
  replied: APPLICANT_STATUS_LABEL.replied,
  shortlisted: APPLICANT_STATUS_LABEL.shortlisted,
  hold: APPLICANT_STATUS_LABEL.hold,
  accepted: APPLICANT_STATUS_LABEL.accepted,
  rejected: APPLICANT_STATUS_LABEL.rejected,
};

/// The per-status counts every funnel row carries. Typed structurally rather
/// than as the row itself so this module never has to import the wire schema.
export type FunnelStatusCounts = Record<FunnelStatusKey, number>;

export interface FunnelStage {
  status: ProgressionStatus;
  key: FunnelStatusKey;
  label: string;
  tone: RecruitmentTone;
  count: number;
  /// Width of the bar, 0 to 1, relative to the WIDEST stage rather than to the
  /// first one. These are current-status snapshots, not cumulative totals, so
  /// a later stage legitimately outgrows an earlier one (everybody who applied
  /// last month has since been shortlisted). Scaling to the first stage would
  /// draw those bars past the end of the chart.
  ratio: number;
}

export interface FunnelAttrition {
  hold: number;
  rejected: number;
  total: number;
}

export interface FunnelSeries {
  stages: FunnelStage[];
  attrition: FunnelAttrition;
  /// Everyone still moving forward. The denominator for "x% of the pipeline".
  progressionTotal: number;
}

/**
 * Splits one funnel row into the four bars that are drawn as a ramp and the
 * attrition that is not.
 *
 * `rejected` is deliberately NOT returned as a stage. Drawn as a sixth bar it
 * would sit at the bottom of a left-to-right ramp, in the place a reader's eye
 * has been trained to read as "the ones we hired", and on a healthy pipeline
 * it is the biggest number of the six. Hold and rejected are returned as one
 * total so a caller can show them beside the funnel, never inside it.
 */
export function deriveFunnelSeries(row: FunnelStatusCounts): FunnelSeries {
  const counts = PROGRESSION_STATUSES.map((status) => {
    const key = FUNNEL_COLUMN_BY_STATUS[status];
    return { status, key, count: row[key] };
  });
  // An all-zero opening (published this morning, nobody has applied) must draw
  // four empty bars, not four full ones, so the fallback denominator is 1.
  const widest = Math.max(...counts.map((entry) => entry.count), 0);
  const scale = widest > 0 ? widest : 1;

  return {
    stages: counts.map((entry) => ({
      status: entry.status,
      key: entry.key,
      label: FUNNEL_STAGE_LABEL[entry.key],
      tone: APPLICANT_STATUS_TONE[entry.status],
      count: entry.count,
      ratio: entry.count / scale,
    })),
    attrition: { hold: row.hold, rejected: row.rejected, total: row.hold + row.rejected },
    progressionTotal: counts.reduce((sum, entry) => sum + entry.count, 0),
  };
}
